import type { Column } from '@tanstack/react-table';
import { type ReactNode, isValidElement } from 'react';

const humanizeColumnId = (id: string): string => id.replace(/_/g, ' ');

type HeaderComponentWithOptionsLabel = {
    optionsLabel?: ReactNode;
};

/**
 * Resolves a human-readable column label for the table options panel.
 * Prefers the column header ReactNode (translated overwrite / default header)
 * over a raw `col.id` string.
 */
export function getColumnOptionsLabel(column: Column<any, unknown>): ReactNode {
    const headerDef = column.columnDef.header;
    if (typeof headerDef === 'string' && headerDef.trim().length > 0) {
        return headerDef;
    }
    if (typeof headerDef === 'function') {
        try {
            const rendered = headerDef({
                column,
                header: column as never,
                table: undefined as never
            });
            if (rendered == null || rendered === false || rendered === true) {
                return humanizeColumnId(column.id);
            }
            // Interactive chrome (selection / expand) is not a useful options label.
            if (column.id === 'selection' || column.id === 'expand') {
                return humanizeColumnId(column.id);
            }
            // Icon-only headers can expose a text label for the Columns checklist.
            if (isValidElement(rendered) && typeof rendered.type !== 'string') {
                const optionsLabel = (rendered.type as HeaderComponentWithOptionsLabel).optionsLabel;
                if (optionsLabel != null) return optionsLabel;
            }
            return rendered as ReactNode;
        } catch {
            return humanizeColumnId(column.id);
        }
    }
    return humanizeColumnId(column.id);
}
